import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { SpecialTaskProvider } from './Components/Elda-Damage/SpecialTask/contexts/SpecialTaskContext'
import Loading from './Components/Layers/Loading'
import ModalLidar from './Components/Layers/modals/ModalLidar'
import MobileLayout from './Components/Mobile/MobileLayout'
import { SmartSearchPalette } from './Components/SmartSearch'
import WhatsNewModal from './Components/WhatsNew/WhatsNewModal'
import { NotificationProvider } from './Components/shared/NotificationProvider'
import { useAuth } from './Contexts/AuthContext'
import { DashboardModalProvider } from './Contexts/DashboardModalContext'
import { useModal } from './Contexts/ModalContext'
import { SmartSearchProvider } from './Contexts/SmartSearchContext'
import { hasStoredUser } from './api/authApi'
import { getLatestUnseen } from './api/whatsNewApi'
import { AppRoutes } from './routes/routes'
import { lightTheme, useThemeMode } from './theme'

const PUBLIC_PATHS = ['/login', '/mdpchange', '/unauthorized']
export const isPublicPath = (pathname: string) =>
  PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))

// plain navy screen matching the login background, shown instead of any loader
export const NavyScreen = () => (
  <div
    className="min-h-screen"
    style={{ backgroundColor: lightTheme.brand.navy }}
  />
)

const AppContent = () => {
  const { isAuthenticated, loading, loggingOut } = useAuth()
  const { pathname } = useLocation()
  const theme = useThemeMode()
  const { modals } = useModal()
  const [whatsNewPost, setWhatsNewPost] = useState<any | null>(null)
  const whatsNewCheckedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || loading || whatsNewCheckedRef.current) return
    whatsNewCheckedRef.current = true
    getLatestUnseen()
      .then(res => {
        if (res?.post) setWhatsNewPost(res.post)
      })
      .catch(() => {
        /* non-critical: skip popup on error */
      })
  }, [isAuthenticated, loading])

  // public pages open on the login navy and the logout ends on it, so no loader
  // flashes before the login fade-in; a visitor with no stored session is headed
  // to the login page as well
  if (loggingOut || (loading && (isPublicPath(pathname) || !hasStoredUser())))
    return <NavyScreen />

  if (loading) {
    return <Loading />
  }

  if (!isAuthenticated) {
    if (!isPublicPath(pathname)) {
      return <Navigate to="/login" replace state={{ from: { pathname } }} />
    }
    return (
      <NotificationProvider theme={theme} position="bottom-center">
        <AppRoutes />
      </NotificationProvider>
    )
  }

  const lidarModal = modals?.lidarModal

  return (
    <DashboardModalProvider>
      <SpecialTaskProvider>
        <SmartSearchProvider>
          <NotificationProvider theme={theme} position="bottom-center">
            <MobileLayout>
              <AppRoutes />
            </MobileLayout>
            <SmartSearchPalette />
          </NotificationProvider>
        </SmartSearchProvider>
      </SpecialTaskProvider>
      {lidarModal && (
        <ModalLidar
          isOpen={lidarModal.isOpen}
          mode={lidarModal.mode ?? 'desktop'}
          onUploadSuccess={lidarModal.onUploadSuccess ?? (() => {})}
          onClose={lidarModal.onClose ?? (() => {})}
        />
      )}
      {whatsNewPost && (
        <WhatsNewModal
          post={whatsNewPost}
          isOpen={!!whatsNewPost}
          onClose={() => setWhatsNewPost(null)}
        />
      )}
    </DashboardModalProvider>
  )
}

export default AppContent
